import { useDispatch, useSelector } from 'react-redux';
import { selectActiveSuggestions } from '../../redux/features/aiSuggestions/aiSuggestions.selector';
import { dismissSuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.slice';
import { IAISuggestion } from '../../redux/features/aiSuggestions/aiSuggestions.props';
import {
  SUGGESTION_TYPE,
  UPSELL_REASON_CODE,
} from '../../redux/features/aiSuggestions/aiSuggestions.constants';
import useSendMessageHooks from '../../hooks/useSendMessage/useSendMessage.hooks';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import {
  AgentInterceptionType,
  messagingActions,
} from '../../reducers/messagingSlice';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagAgentCart,
} from '../../redux/features/config/config.selector';
import { selectTopLevelMenuItems } from '../../selectors/menu';
import { addItemToCart } from '../../reducers/menuSlice';
import { removeSlashCommandByIntent } from '../../reducers/dialogSlice';
import { SLASH_COMMAND_REMINDERS } from '../../utils/constants';
import logger from '../../utils/logger';

/**
 * Accept / dismiss logic for AI suggestions, extracted so the card buttons and
 * the Alt+A / Alt+D keyboard shortcuts drive the exact same path. The human
 * agent stays the driver — a suggestion is only ever applied on an explicit
 * accept.
 */
const useAISuggestionActions = () => {
  const dispatch = useDispatch();
  const suggestions = useSelector(selectActiveSuggestions);
  const isHitl3 = useSelector(selectFeatureFlagHitl3);
  const isAgentCart = useSelector(selectFeatureFlagAgentCart);
  const topLevelMenuItems = useSelector(selectTopLevelMenuItems) || {};
  const { sendMessage } = useSendMessageHooks();
  const { sendAgentInterception } = useAgentInterception();

  // Only the latest suggestion is ever displayed; treat it as the active one
  // that the keyboard shortcuts act on.
  const activeSuggestion: IAISuggestion | undefined =
    suggestions[suggestions.length - 1];

  /**
   * Emit the HITL -> AI response the backend consumes to close the co-pilot
   * loop. hitl_3_0 only. The envelope (event/agent_type/session_id) is stamped
   * by the messaging middleware; we only supply `data`. `verbatim` is always
   * true today — the accepted line is spoken unchanged (no edit-before-speak
   * flow yet).
   */
  const emitSuggestionResponse = (
    suggestion: IAISuggestion,
    action: 'accepted' | 'dismissed'
  ) => {
    if (!isHitl3) return;
    dispatch(
      messagingActions.sendAgentSuggestionResponse({
        data: {
          action,
          suggestion_id: suggestion.suggestion_id,
          request_id: suggestion.request_id,
          ...(action === 'accepted' && suggestion.tts
            ? { spoken_text: suggestion.tts, verbatim: true }
            : null),
        },
      })
    );
  };

  /**
   * Donation post-readback upsell (hitl_3_0 + agent_cart only). On Accept, add
   * the donation to the cart *exactly* as pressing Enter on the OmniBar donation
   * slash command does today — the same `/upsell_donation` top-level item, the
   * same `addItemToCart` thunk, the same ORDER_CHANGED interception.
   *
   * Donation stays in the slash-command queue (so the OmniBar keeps showing it),
   * but is hidden from the Reminder chip text. Accepting here fulfils it, so we
   * also remove it from the queue — the OmniBar drops it from its placeholder /
   * auto-add and there's no double-add.
   *
   * Scope is donation only for now: classify on offer_upsell + POST_READBACK.
   * The other upsell reason codes (END_OF_ORDER / UPSELL_AVAILABLE) are out of
   * scope and intentionally not handled here.
   */
  const maybeAddDonationToCart = (suggestion: IAISuggestion) => {
    if (!isHitl3 || !isAgentCart) return;
    if (
      suggestion.suggestion_type !== SUGGESTION_TYPE.OFFER_UPSELL ||
      suggestion.reason_code !== UPSELL_REASON_CODE.POST_READBACK
    ) {
      return;
    }

    // Resolve the same top-level item the OmniBar adds (fixed cross-brand key).
    const intent = SLASH_COMMAND_REMINDERS.DONATION;
    const matchingItem = Object.values(topLevelMenuItems).find(
      (item) =>
        !item.inactive && (item.name === intent || item.name.startsWith(intent))
    );
    // Fail-safe: brand has no active donation prompt → nothing to add.
    if (!matchingItem) return;

    dispatch(
      addItemToCart({
        menuItem: matchingItem,
        quantity: 1,
        byPassAutoPrompt: true, // tts already spoken on accept — don't re-speak
      })
    );
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.ORDER_CHANGED,
        description: `${matchingItem.name} auto-added (ai_suggestion accepted)`,
      },
    });
    // Fulfilled here → drop it from the slash-command queue so the OmniBar
    // removes it from its placeholder / auto-add (and no double-add).
    dispatch(removeSlashCommandByIntent(intent));
    logger.info({
      message: `AI donation auto-added: ${matchingItem.name} (${suggestion.suggestion_id})`,
    });
  };

  const accept = (suggestion: IAISuggestion) => {
    if (suggestion.tts) {
      // Any suggestion that carries a `tts` line is speakable, regardless of its
      // suggestion_type. Speak it via the normal HITL send path (status=final
      // -> TTS), and mark the agent as having taken the turn (TTS_SENT), exactly
      // like an intent pill / OmniBar send.
      sendAgentInterception({
        data: { type: AgentInterceptionType.TTS_SENT },
      });
      sendMessage(suggestion.tts, { intent: 'ai_suggestion' });
      logger.info({
        message: `AI suggestion accepted (tts): "${suggestion.tts}" (${suggestion.suggestion_id})`,
      });
    } else {
      // TODO (step 2): map suggestion.action -> a cartSlice mutation
      // (selectModifier / addItemToCart) for cart-action suggestions.
      logger.info({
        message: `AI suggestion accepted: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
        moreInfo: JSON.stringify(suggestion.action),
      });
    }
    maybeAddDonationToCart(suggestion);
    emitSuggestionResponse(suggestion, 'accepted');
    dispatch(
      dismissSuggestion({
        suggestionType: suggestion.suggestion_type,
        suggestionId: suggestion.suggestion_id,
      })
    );
  };

  const dismiss = (suggestion: IAISuggestion) => {
    logger.info({
      message: `AI suggestion dismissed: ${suggestion.suggestion_type} (${suggestion.suggestion_id})`,
    });
    emitSuggestionResponse(suggestion, 'dismissed');
    dispatch(
      dismissSuggestion({
        suggestionType: suggestion.suggestion_type,
        suggestionId: suggestion.suggestion_id,
      })
    );
  };

  return { suggestions, activeSuggestion, accept, dismiss };
};

export default useAISuggestionActions;
